(() => {
  'use strict';

  const collection = (handle, label) => ({
    href: `/collections/${handle}`,
    label,
  });

  const collections = {
    mural: collection('porte-manteau-mural', 'Voir les porte-manteaux muraux'),
    sansPercage: collection('porte-manteau-sans-percage', 'Voir les modèles sans perçage'),
    surPied: collection('porte-manteau-sur-pied', 'Comparer avec les porte-manteaux sur pied'),
    grandeCapacite: collection('porte-manteau-grande-capacite', 'Voir les modèles grande capacité'),
    vestiaire: collection('porte-manteau-meuble-chaussure', 'Voir les vestiaires combinés'),
    banc: collection('meuble-a-chaussures-avec-banc', 'Voir les meubles avec banc'),
    miroir: collection('meuble-a-chaussures-avec-miroir', 'Voir les meubles avec miroir'),
    etagere: collection('meuble-a-chaussures-avec-etagere', 'Voir les meubles avec étagère'),
    coatWood: collection('porte-manteau-bois', 'Explorer les porte-manteaux en bois'),
    coatLight: collection('porte-manteau-blanc', 'Explorer les porte-manteaux clairs'),
    coatDark: collection('porte-manteau-noir', 'Explorer les porte-manteaux noirs'),
    shoeWood: collection('meuble-a-chaussures-bois', 'Explorer les meubles en bois'),
    shoeLight: collection('meuble-a-chaussures-blanc', 'Explorer les meubles clairs'),
    shoeDark: collection('meuble-a-chaussures-noir', 'Explorer les meubles noirs'),
  };

  const commonQuestions = [
    {
      key: 'need',
      title: 'Que souhaitez-vous mieux organiser ?',
      help: 'Choisissez ce qui manque le plus à votre entrée aujourd’hui.',
      options: [
        { value: 'coats', label: 'Les manteaux et les sacs', detail: 'Les accrocher, les retrouver facilement' },
        { value: 'shoes', label: 'Les chaussures', detail: 'Les ranger près de la porte' },
        { value: 'both', label: 'Les deux', detail: 'Créer un vrai point de départ' },
      ],
    },
    {
      key: 'space',
      title: 'Quelle largeur libre avez-vous ?',
      help: 'Mesurez avec les portes ouvertes et gardez un passage agréable.',
      options: [
        { value: 'narrow', label: 'Moins de 80 cm', detail: 'Un passage à préserver' },
        { value: 'medium', label: 'De 80 à 140 cm', detail: 'Un pan de mur disponible' },
        { value: 'wide', label: 'Plus de 140 cm', detail: 'Une entrée à composer' },
      ],
    },
  ];

  const fixingQuestion = {
    key: 'fix',
    title: 'Pouvez-vous fixer au mur ?',
    help: 'La bonne fixation dépendra toujours du mur et du modèle choisi.',
    options: [
      { value: 'yes', label: 'Oui', detail: 'Une fixation murale est possible' },
      { value: 'no', label: 'Non', detail: 'Je préfère une solution sans perçage' },
      { value: 'unsure', label: 'Je ne sais pas encore', detail: 'Je veux garder les deux pistes' },
    ],
  };

  const styleQuestion = {
    key: 'style',
    title: 'Quelle allure vous attire ?',
    help: 'Nous vous montrerons aussi une famille à explorer selon cette préférence.',
    options: [
      { value: 'wood', label: 'Bois chaleureux', detail: 'Une matière qui donne du relief' },
      { value: 'light', label: 'Clair et discret', detail: 'Une présence légère dans l’entrée' },
      { value: 'dark', label: 'Noir graphique', detail: 'Un contraste plus affirmé' },
    ],
  };

  const branchQuestions = {
    coats: [
      fixingQuestion,
      {
        key: 'coatLoad',
        title: 'Qu’accrochez-vous au quotidien ?',
        help: 'Ce geste aide à choisir la bonne famille de porte-manteaux.',
        options: [
          { value: 'light', label: 'Quelques vestes et sacs', detail: 'L’essentiel à portée de main' },
          { value: 'family', label: 'Pour tout le foyer', detail: 'Plusieurs manteaux et accessoires' },
          { value: 'mixed', label: 'Vestes et chaussures', detail: 'Deux usages au même endroit' },
        ],
      },
      styleQuestion,
    ],
    shoes: [
      {
        key: 'shoeFeature',
        title: 'Quel détail vous serait le plus utile ?',
        help: 'Choisissez le geste que le meuble doit faciliter.',
        options: [
          { value: 'bench', label: 'Pouvoir m’asseoir', detail: 'Un banc pour se chausser' },
          { value: 'mirror', label: 'Avoir un miroir', detail: 'Un dernier regard avant de sortir' },
          { value: 'shelf', label: 'Poser les petits objets', detail: 'Une étagère d’appoint' },
        ],
      },
      {
        key: 'shoeVolume',
        title: 'Pour qui rangez-vous les chaussures ?',
        help: 'La capacité exacte se vérifie sur chaque fiche produit.',
        options: [
          { value: 'few', label: 'Une ou deux personnes', detail: 'Les paires du quotidien' },
          { value: 'family', label: 'Toute la famille', detail: 'Plusieurs habitudes à réunir' },
          { value: 'many', label: 'Une grande collection', detail: 'Comparer attentivement la capacité' },
        ],
      },
      styleQuestion,
    ],
    both: [
      {
        key: 'arrangement',
        title: 'Comment imaginez-vous l’ensemble ?',
        help: 'Un vestiaire complet ou deux pièces que vous pouvez composer.',
        options: [
          { value: 'single', label: 'Un seul meuble', detail: 'Tout réunir dans une pièce' },
          { value: 'duo', label: 'Deux éléments', detail: 'Adapter chaque fonction à sa place' },
          { value: 'unsure', label: 'Je veux comparer', detail: 'Voir les deux possibilités' },
        ],
      },
      fixingQuestion,
      {
        key: 'bothFeature',
        title: 'Quel détail compléterait l’entrée ?',
        help: 'Il guidera la deuxième piste que nous vous proposerons.',
        options: [
          { value: 'bench', label: 'Une assise', detail: 'Se chausser confortablement' },
          { value: 'mirror', label: 'Un miroir', detail: 'Associer rangement et dernier regard' },
          { value: 'shelf', label: 'Une étagère', detail: 'Poser clés et petits objets' },
        ],
      },
    ],
  };

  const coatStyle = { wood: collections.coatWood, light: collections.coatLight, dark: collections.coatDark };
  const shoeStyle = { wood: collections.shoeWood, light: collections.shoeLight, dark: collections.shoeDark };
  const shoeFeature = { bench: collections.banc, mirror: collections.miroir, shelf: collections.etagere };
  const widthPhrase = {
    narrow: 'Avec moins de 80 cm, comparez particulièrement la largeur et la profondeur de chaque modèle.',
    medium: 'Avec 80 à 140 cm, vérifiez la largeur du meuble et l’espace nécessaire pour circuler.',
    wide: 'Avec plus de 140 cm, comparez les compositions sans perdre de vue la circulation.',
  };

  function questionsFor(need) {
    return [...commonQuestions, ...(branchQuestions[need] || branchQuestions.coats)];
  }

  function recommend(answers) {
    const width = widthPhrase[answers.space];

    if (answers.need === 'coats') {
      const style = coatStyle[answers.style];
      const styleName = { wood: 'le bois chaleureux', light: 'les teintes claires', dark: 'le noir graphique' }[answers.style];
      if (answers.coatLoad === 'mixed') {
        if (answers.space !== 'narrow' && answers.fix === 'yes') {
          return {
            title: 'Un vestiaire pour les deux gestes du départ.',
            description: `Vous voulez réunir vestes et chaussures. Explorez les vestiaires combinés, puis comparez leur encombrement et leur pose. ${width}`,
            primary: collections.vestiaire,
            secondary: shoeStyle[answers.style],
          };
        }
        return {
          title: 'Deux pièces à composer selon votre entrée.',
          description: `Pour séparer les deux usages, commencez par un porte-manteau ${answers.fix === 'yes' ? 'mural' : 'sans perçage'}, puis choisissez un rangement à chaussures dans l’allure souhaitée. ${width}`,
          primary: answers.fix === 'yes' ? collections.mural : collections.sansPercage,
          secondary: shoeStyle[answers.style],
        };
      }
      if (answers.fix !== 'yes') {
        return {
          title: 'Commencer sans fixation murale.',
          description: `${answers.coatLoad === 'family' ? 'Vous avez plusieurs manteaux et accessoires à accueillir.' : 'Vous cherchez une place simple pour quelques vestes et sacs.'} Explorez les modèles sans perçage, puis vérifiez leur stabilité et leur capacité. Gardez votre préférence pour ${styleName} en comparant les finitions disponibles. ${width}`,
          primary: collections.sansPercage,
          secondary: answers.fix === 'no' ? collections.surPied : style,
        };
      }
      if (answers.coatLoad === 'family') {
        return {
          title: 'Une piste pour plusieurs manteaux.',
          description: `Votre foyer a besoin de plusieurs points d’accroche. Parcourez les modèles grande capacité et vérifiez le type de fixation ainsi que la charge indiquée pour chacun. ${width}`,
          primary: collections.grandeCapacite,
          secondary: style,
        };
      }
      return {
        title: 'Le mur pour garder l’entrée légère.',
        description: `Pour quelques vestes et sacs, un porte-manteau mural est un point de départ discret. Vérifiez que votre support accepte sa fixation. ${width}`,
        primary: collections.mural,
        secondary: style,
      };
    }

    if (answers.need === 'shoes') {
      const featureText = {
        bench: 'une assise pour se chausser',
        mirror: 'un miroir près de la porte',
        shelf: 'une étagère où poser les petits objets',
      }[answers.shoeFeature];
      const volumeText = {
        few: 'Pour une ou deux personnes, comparez les compartiments utiles plutôt que l’encombrement seul.',
        family: 'Pour toute la famille, regardez le nombre de paires réellement accueilli par chaque modèle.',
        many: 'Pour une grande collection, vérifiez la capacité, les dimensions intérieures et l’accès à chaque paire.',
      }[answers.shoeVolume];
      return {
        title: {
          bench: 'Un banc qui fait aussi de la place aux chaussures.',
          mirror: 'Rangement et miroir au même endroit.',
          shelf: 'Des chaussures rangées, les petits objets à portée.',
        }[answers.shoeFeature],
        description: `Vous cherchez ${featureText}. ${volumeText} ${width}`,
        primary: shoeFeature[answers.shoeFeature],
        secondary: shoeStyle[answers.style],
      };
    }

    const shoe = shoeFeature[answers.bothFeature];
    const needsDuo = answers.arrangement === 'duo' || answers.space === 'narrow' || answers.fix !== 'yes' || (answers.arrangement === 'unsure' && answers.space === 'medium');
    if (needsDuo) {
      const coat = answers.fix === 'yes' ? collections.mural : collections.sansPercage;
      const reason = answers.fix === 'no'
        ? 'Sans perçage, partez d’un porte-manteau adapté à cette contrainte.'
        : answers.space === 'narrow'
          ? 'Dans un passage étroit, deux éléments laissent davantage de liberté pour composer l’ensemble.'
          : 'Deux éléments permettent de choisir séparément l’accueil des manteaux et celui des chaussures.';
      return {
        title: 'Composer l’entrée en deux pièces.',
        description: `${reason} Ajoutez ensuite le meuble à chaussures avec ${answers.bothFeature === 'bench' ? 'banc' : answers.bothFeature === 'mirror' ? 'miroir' : 'étagère'} qui répond à votre dernier choix. ${width}`,
        primary: coat,
        secondary: shoe,
      };
    }
    return {
      title: 'Un vestiaire pour réunir manteaux et chaussures.',
      description: `Vous préférez un seul meuble et disposez d’un espace à composer. Explorez les vestiaires complets ; la deuxième piste vous permet de comparer les modèles avec ${answers.bothFeature === 'bench' ? 'banc' : answers.bothFeature === 'mirror' ? 'miroir' : 'étagère'} si ce détail est décisif. ${width}`,
      primary: collections.vestiaire,
      secondary: shoe,
    };
  }

  function initQuiz(root) {
    if (!root || root.dataset.bvInitialized === 'true') return;
    root.dataset.bvInitialized = 'true';
  const elements = {
    progressLabel: root.querySelector('[data-quiz-progress-label]'),
    progressTrack: root.querySelector('[data-quiz-progress-track]'),
    progressFill: root.querySelector('[data-quiz-progress-fill]'),
    step: root.querySelector('[data-quiz-step]'),
    question: root.querySelector('[data-quiz-question]'),
    help: root.querySelector('[data-quiz-help]'),
    options: root.querySelector('[data-quiz-options]'),
    result: root.querySelector('[data-quiz-result]'),
    resultTitle: root.querySelector('[data-quiz-result-title]'),
    resultDescription: root.querySelector('[data-quiz-result-description]'),
    resultSummary: root.querySelector('[data-quiz-result-summary]'),
    primaryLink: root.querySelector('[data-quiz-primary-link]'),
    secondaryLink: root.querySelector('[data-quiz-secondary-link]'),
    back: root.querySelector('[data-quiz-back]'),
    reset: root.querySelector('[data-quiz-reset]'),
  };

  let answers = {};
  let stepIndex = 0;

  function setProgress(value, label) {
    elements.progressLabel.textContent = label;
    elements.progressTrack.setAttribute('aria-valuenow', String(value));
    elements.progressFill.style.width = `${(value / 5) * 100}%`;
  }

  function setLink(element, link) {
    element.href = link.href;
    element.textContent = link.label;
  }

  function renderResult(moveFocus) {
    const result = recommend(answers);
    elements.step.hidden = true;
    elements.result.hidden = false;
    elements.back.hidden = false;
    elements.reset.hidden = false;
    setProgress(5, 'Votre sélection');
    elements.resultTitle.textContent = result.title;
    elements.resultDescription.textContent = result.description;
    setLink(elements.primaryLink, result.primary);
    setLink(elements.secondaryLink, result.secondary);
    elements.resultSummary.replaceChildren();
    questionsFor(answers.need).forEach((question) => {
      const selected = question.options.find((option) => option.value === answers[question.key]);
      if (!selected) return;
      const chip = document.createElement('span');
      chip.textContent = selected.label;
      elements.resultSummary.append(chip);
    });
    if (moveFocus) elements.resultTitle.focus({ preventScroll: true });
  }

  function renderQuestion(moveFocus) {
    const question = questionsFor(answers.need)[stepIndex];
    elements.step.hidden = false;
    elements.result.hidden = true;
    elements.back.hidden = stepIndex === 0;
    elements.reset.hidden = stepIndex === 0;
    setProgress(stepIndex + 1, `Question ${stepIndex + 1} sur 5`);
    elements.question.textContent = question.title;
    elements.help.textContent = question.help;
    elements.options.replaceChildren();
    question.options.forEach((option, index) => {
      const button = document.createElement('button');
      const number = document.createElement('span');
      const copy = document.createElement('span');
      const label = document.createElement('strong');
      const detail = document.createElement('small');
      button.type = 'button';
      button.className = 'quiz-option';
      button.setAttribute('aria-pressed', String(answers[question.key] === option.value));
      if (answers[question.key] === option.value) button.classList.add('is-selected');
      number.className = 'quiz-option-number';
      number.textContent = `0${index + 1}`;
      copy.className = 'quiz-option-copy';
      label.textContent = option.label;
      detail.textContent = option.detail;
      copy.append(label, detail);
      button.append(number, copy);
      button.addEventListener('click', () => {
        if (question.key === 'need' && answers.need && answers.need !== option.value) answers = {};
        answers[question.key] = option.value;
        stepIndex += 1;
        render(true);
      });
      elements.options.append(button);
    });
    if (moveFocus) elements.question.focus({ preventScroll: true });
  }

  function render(moveFocus = false) {
    if (stepIndex >= 5) renderResult(moveFocus);
    else renderQuestion(moveFocus);
  }

  elements.back.addEventListener('click', () => {
    stepIndex = Math.max(0, stepIndex - 1);
    render(true);
  });
  elements.reset.addEventListener('click', () => {
    answers = {};
    stepIndex = 0;
    render(true);
  });
  render();
  }

  function initGuide(root) {
    if (!root || root.dataset.bvInitialized === 'true') return;
    root.dataset.bvInitialized = 'true';
    const input = root.querySelector('[data-width-input]');
    const output = root.querySelector('[data-width-output]');
    const visualWidth = root.querySelector('[data-visual-width]');
    const label = root.querySelector('[data-result-label]');
    const title = root.querySelector('[data-result-title]');
    const description = root.querySelector('[data-result-description]');
    const link = root.querySelector('[data-result-link]');
    const presets = [...root.querySelectorAll('[data-width]')];
    const photos = [...root.querySelectorAll('.selector-photo')];
    const recommendations = {
      small: { label: 'Piste de départ · Le mur', title: 'Commencez par le porte-manteau mural.', description: 'Il garde le sol disponible et laisse davantage de liberté dans un passage étroit.', action: 'Voir les modèles muraux', href: root.dataset.smallHref },
      medium: { label: 'Piste de départ · L’assise', title: 'Faites du rangement un endroit où s’asseoir.', description: 'Un banc à chaussures rassemble deux gestes au même endroit, si la profondeur laisse le passage libre.', action: 'Voir les meubles avec banc', href: root.dataset.mediumHref },
      large: { label: 'Piste de départ · Le sol', title: 'Donnez une vraie place aux manteaux.', description: 'Un modèle sur pied peut structurer une entrée plus généreuse sans utiliser la surface du mur.', action: 'Voir les modèles sur pied', href: root.dataset.largeHref }
    };
    function render() {
      const width = Number(input.value);
      const scene = width < 80 ? 'small' : width < 140 ? 'medium' : 'large';
      const selected = recommendations[scene];
      output.value = String(width);
      output.textContent = String(width);
      visualWidth.textContent = String(width);
      label.textContent = selected.label;
      title.textContent = selected.title;
      description.textContent = selected.description;
      link.textContent = selected.action;
      link.href = selected.href;
      photos.forEach((photo) => {
        const active = photo.dataset.scene === scene;
        photo.classList.toggle('is-active', active);
        photo.setAttribute('aria-hidden', String(!active));
      });
      presets.forEach((button) => {
        const active = Number(button.dataset.width) === width;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }
    input.addEventListener('input', render);
    presets.forEach((button) => button.addEventListener('click', () => { input.value = button.dataset.width; render(); }));
    render();
  }

  function initNumbers(root) {
    if (!root || root.dataset.bvInitialized === 'true') return;
    root.dataset.bvInitialized = 'true';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
    const counters = root.querySelectorAll('.count[data-count]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const element = entry.target;
        const target = Number(element.dataset.count);
        const started = performance.now();
        const duration = 1050;
        const animate = (now) => {
          const progress = Math.min(1, (now - started) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          element.textContent = String(Math.round(target * eased));
          if (progress < 1 && element.isConnected) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.unobserve(element);
      });
    }, { threshold: 0.55 });
    counters.forEach((counter) => observer.observe(counter));
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-bv-guide]').forEach(initGuide);
    scope.querySelectorAll('[data-bv-numbers]').forEach(initNumbers);
    scope.querySelectorAll('[data-bv-quiz]').forEach(initQuiz);
  }

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
